/**
 * ScheduleConfig Component
 *
 * Configuration component for automation scheduling.
 * Allows users to select days of the week, time of day, and timezone
 * for when automation rules should run.
 */

import { memo, useCallback, useMemo } from 'react';
import type { IScheduleConfigProps } from '@/@types/automation';
import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';

/** Day of week configuration */
interface IDayConfig {
  value: number;
  label: string;
  full: string;
}

/** Timezone configuration */
interface ITimezoneConfig {
  value: string;
  label: string;
}

const DAYS: IDayConfig[] = [
  { value: 0, label: 'S', full: 'Sunday' },
  { value: 1, label: 'M', full: 'Monday' },
  { value: 2, label: 'T', full: 'Tuesday' },
  { value: 3, label: 'W', full: 'Wednesday' },
  { value: 4, label: 'T', full: 'Thursday' },
  { value: 5, label: 'F', full: 'Friday' },
  { value: 6, label: 'S', full: 'Saturday' },
];

const TIMEZONES: ITimezoneConfig[] = [
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'America/Phoenix', label: 'Arizona (MST)' },
  { value: 'America/Anchorage', label: 'Alaska Time' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time' },
  { value: 'Europe/London', label: 'London (GMT/BST)' },
  { value: 'Europe/Paris', label: 'Paris (CET)' },
  { value: 'Europe/Berlin', label: 'Berlin (CET)' },
  { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
  { value: 'Asia/Shanghai', label: 'Shanghai (CST)' },
  { value: 'Australia/Sydney', label: 'Sydney (AEST)' },
  { value: 'UTC', label: 'UTC' },
];

/**
 * ScheduleConfig component for configuring automation schedule
 *
 * Features:
 * - Day of week toggles (S M T W T F S)
 * - Time input in HH:MM format
 * - Timezone selector with common timezones
 * - Next run preview calculation
 */
export const ScheduleConfig = memo(function ScheduleConfig({
  schedule,
  onChange,
  className,
}: IScheduleConfigProps) {
  /** Toggle a day selection on/off */
  const handleDayToggle = useCallback(
    (day: number) => {
      const newDays = schedule.daysOfWeek.includes(day)
        ? schedule.daysOfWeek.filter(d => d !== day)
        : [...schedule.daysOfWeek, day].sort((a, b) => a - b);

      onChange({ ...schedule, daysOfWeek: newDays });
    },
    [schedule, onChange]
  );

  /** Update the time of day */
  const handleTimeChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange({ ...schedule, timeOfDay: e.target.value });
    },
    [schedule, onChange]
  );

  /** Update the timezone */
  const handleTimezoneChange = useCallback(
    (value: string) => {
      onChange({ ...schedule, timezone: value });
    },
    [schedule, onChange]
  );

  /** Calculate and format the next scheduled run time */
  const nextRunPreview = useMemo(() => {
    if (schedule.daysOfWeek.length === 0) {
      return null;
    }

    const [hoursStr, minutesStr] = schedule.timeOfDay.split(':');
    const hours = parseInt(hoursStr, 10);
    const minutes = parseInt(minutesStr, 10);

    if (isNaN(hours) || isNaN(minutes)) {
      return null;
    }

    const now = new Date();

    // Find next scheduled day within the next 7 days
    for (let i = 0; i < 7; i++) {
      const checkDate = new Date(now);
      checkDate.setDate(now.getDate() + i);
      const dayOfWeek = checkDate.getDay();

      if (schedule.daysOfWeek.includes(dayOfWeek)) {
        checkDate.setHours(hours, minutes, 0, 0);
        if (checkDate > now) {
          return checkDate.toLocaleString('en-US', {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            timeZone: schedule.timezone,
          });
        }
      }
    }

    // If no future time found in first pass, check next week's first scheduled day
    for (let i = 7; i < 14; i++) {
      const checkDate = new Date(now);
      checkDate.setDate(now.getDate() + i);
      const dayOfWeek = checkDate.getDay();

      if (schedule.daysOfWeek.includes(dayOfWeek)) {
        checkDate.setHours(hours, minutes, 0, 0);
        return checkDate.toLocaleString('en-US', {
          weekday: 'short',
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
          timeZone: schedule.timezone,
        });
      }
    }

    return null;
  }, [schedule]);

  return (
    <div className={cn('space-y-4', className)}>
      {/* Days of Week */}
      <div className="space-y-2">
        <Label>Days of Week</Label>
        <div className="flex gap-1">
          {DAYS.map(day => {
            const isSelected = schedule.daysOfWeek.includes(day.value);
            return (
              <Button
                key={day.value}
                type="button"
                variant={isSelected ? 'default' : 'outline'}
                size="sm"
                className={cn('h-9 w-9 p-0', isSelected && 'ring-2 ring-primary ring-offset-2')}
                onClick={() => handleDayToggle(day.value)}
                title={day.full}
                aria-label={`${isSelected ? 'Deselect' : 'Select'} ${day.full}`}
                aria-pressed={isSelected}
              >
                {day.label}
              </Button>
            );
          })}
        </div>
        {schedule.daysOfWeek.length === 0 && (
          <p className="text-sm text-destructive">Select at least one day</p>
        )}
      </div>

      {/* Time and Timezone */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="schedule-time">Time</Label>
          <Input
            id="schedule-time"
            type="time"
            value={schedule.timeOfDay}
            onChange={handleTimeChange}
            className="w-full"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="schedule-timezone">Timezone</Label>
          <Select value={schedule.timezone} onValueChange={handleTimezoneChange}>
            <SelectTrigger id="schedule-timezone" className="w-full">
              <SelectValue placeholder="Select timezone" />
            </SelectTrigger>
            <SelectContent>
              {TIMEZONES.map(tz => (
                <SelectItem key={tz.value} value={tz.value}>
                  {tz.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Next Run Preview */}
      {nextRunPreview && (
        <div className="rounded-md bg-muted p-3">
          <p className="text-sm">
            <span className="text-muted-foreground">Next run: </span>
            <span className="font-medium">{nextRunPreview}</span>
          </p>
        </div>
      )}
    </div>
  );
});
